import { useState } from 'react'
import { useChannelHotels, useConfig, useHotelCompetitors, useSynchronizeChannel } from '@/api/queries'
import type { ChannelMetrics, CompetitorMetrics, HotelMetrics } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge, ratioTone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { BOOKING_ENGINE_CHANNEL_ID } from '@/features/settings/policyTarget'
import { percent } from '@/lib/format'
import { links } from '@/lib/links'

interface ChannelDetailsDialogProps {
  channel: Pick<ChannelMetrics, 'channelId' | 'name' | 'posType'>
  hours: number
  onClose: () => void
}


/** Hotels scraped on one site, each expandable into its competitors. */
export function ChannelDetailsDialog({ channel, hours, onClose }: ChannelDetailsDialogProps) {
  const bookingEngine = channel.channelId === BOOKING_ENGINE_CHANNEL_ID ? channel.name : undefined
  const { data, isLoading, error } = useChannelHotels(channel.channelId, bookingEngine, hours)
  const { data: config } = useConfig()
  const synchronize = useSynchronizeChannel()
  const toast = useToast()
  const [confirmSync, setConfirmSync] = useState(false)

  const columns: Column<HotelMetrics>[] = [
    { key: 'hotelName', header: 'Hotel', render: (h) => <strong>{h.hotelName || h.hotelId}</strong> },
    { key: 'hotelId', header: 'Id', align: 'right' },
    { key: 'successRatio', header: 'Success', align: 'right', render: (h) => <Badge tone={ratioTone(h.successRatio, { warnBelow: 80 })}>{percent(h.successRatio)}</Badge> },
    { key: 'openRatio', header: 'Open', align: 'right', render: (h) => percent(h.openRatio) },
    { key: 'closedRatio', header: 'Closed', align: 'right', render: (h) => <Badge tone={ratioTone(h.closedRatio, { dangerAbove: 30 })}>{percent(h.closedRatio)}</Badge> },
    { key: 'notFoundRatio', header: 'Not found', align: 'right', render: (h) => <Badge tone={ratioTone(h.notFoundRatio, { dangerAbove: 10 })}>{percent(h.notFoundRatio)}</Badge> },
    {
      key: 'backoffice',
      header: '',
      sortable: false,
      width: '40px',
      render: (h) => {
        const href = links.hotelBackoffice(config?.links, h.hotelId)
        return href ? (
          <a href={href} target="_blank" rel="noreferrer" title="Open in the D-EDGE platform" className="u-row">
            <Icons.External size={14} />
          </a>
        ) : null
      },
    },
  ]

  const runSynchronize = async () => {
    setConfirmSync(false)
    try {
      const result = await synchronize.mutateAsync({ channelId: channel.channelId, bookingEngine })
      toast.notify(result.accepted ? `Synchronisation of ${channel.name} requested` : `Synchronisation refused: ${result.response}`, result.accepted ? 'success' : 'error')
    } catch (err) {
      toast.error(err, 'Synchronisation failed')
    }
  }

  return (
    <Modal
      title={`${channel.name} · hotels (last ${hours}h)`}
      onClose={onClose}
      size="xl"
      headerExtra={
        <Button size="sm" variant="primary" icon={<Icons.Sync size={13} />} onClick={() => setConfirmSync(true)} loading={synchronize.isPending}>
          Synchronize
        </Button>
      }
    >
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(h) => h.hotelId}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search a hotel by name or id"
        dense
        expandable={(hotel) => <CompetitorsPanel channel={channel} bookingEngine={bookingEngine} hotelId={hotel.hotelId} hours={hours} />}
      />
      {confirmSync && (
        <ConfirmDialog
          title="Synchronize all hotels"
          message={`Start a synchronisation of every hotel on ${channel.name} now?`}
          confirmLabel="Synchronize"
          onCancel={() => setConfirmSync(false)}
          onConfirm={() => void runSynchronize()}
        />
      )}
    </Modal>
  )
}

function CompetitorsPanel({ channel, bookingEngine, hotelId, hours }: { channel: Pick<ChannelMetrics, 'channelId'>; bookingEngine?: string; hotelId: number; hours: number }) {
  const { data, isLoading, error } = useHotelCompetitors(channel.channelId, bookingEngine, hotelId, hours, true)

  const columns: Column<CompetitorMetrics>[] = [
    {
      key: 'competitorName',
      header: 'Competitor',
      render: (c) => (c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.competitorName || c.competitorId}</a> : c.competitorName || c.competitorId),
    },
    { key: 'competitorId', header: 'Id', align: 'right' },
    { key: 'successRatio', header: 'Success', align: 'right', render: (c) => <Badge tone={ratioTone(c.successRatio, { warnBelow: 80 })}>{percent(c.successRatio)}</Badge> },
    { key: 'openRatio', header: 'Open', align: 'right', render: (c) => percent(c.openRatio) },
    { key: 'closedRatio', header: 'Closed', align: 'right', render: (c) => <Badge tone={ratioTone(c.closedRatio, { dangerAbove: 30 })}>{percent(c.closedRatio)}</Badge> },
    { key: 'notFoundRatio', header: 'Not found', align: 'right', render: (c) => <Badge tone={ratioTone(c.notFoundRatio, { dangerAbove: 10 })}>{percent(c.notFoundRatio)}</Badge> },
  ]

  return <DataTable columns={columns} rows={data} rowKey={(c) => c.competitorId} loading={isLoading} error={error} dense maxHeight="260px" emptyMessage="No competitors scraped in this window" />
}
